/**
 * Duration helpers for HTML5 media elements.
 *
 * `videoEl.duration` is not immediately trustworthy: it is NaN before metadata
 * loads, and Infinity when the response has no known length (no Content-Length
 * or no byte-range support). While a video is still buffering, the browser can
 * also report a short provisional duration that grows as more data arrives, so
 * callers should keep the largest value seen for a source rather than latching
 * the first one.
 */

function isPositiveFiniteNumber(value) {
  return typeof value === 'number' && Number.isFinite(value) && value > 0;
}

/**
 * Best available playback length in seconds, or null when nothing is known yet.
 * Falls back to the end of the seekable range when `duration` is unusable.
 */
export function readVideoDurationSec(videoEl) {
  if (!videoEl) return null;

  if (isPositiveFiniteNumber(videoEl.duration)) {
    return videoEl.duration;
  }

  const seekable = videoEl.seekable;
  if (seekable && seekable.length > 0) {
    const end = seekable.end(seekable.length - 1);
    if (isPositiveFiniteNumber(end)) {
      return end;
    }
  }

  return null;
}

/**
 * Duration to keep after a new reading, ignoring provisional values that are
 * shorter than what has already been observed for the current source.
 */
export function mergeVideoDurationSec(previousSec, nextSec) {
  if (!isPositiveFiniteNumber(nextSec)) {
    return previousSec;
  }
  if (!isPositiveFiniteNumber(previousSec)) {
    return nextSec;
  }
  return nextSec > previousSec ? nextSec : previousSec;
}
